import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Heading, InfoSignIcon, Pane, Paragraph } from "evergreen-ui";
import {
    AbstractTableViewProvider
} from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { CostMetadataTableRow } from "../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { CostMetadata, CostPayoutModifier, ShopifyProductVariant } from "../../../../common/models";
import { CostMetadataCreateInput } from "../../../../common/inputs/CostMetadataInput";
import { QueryInput } from "../../../../common/query-filters";
import {
    queryCostMetadataByShopifyProductId,
    useCreateCostMetadataMutation,
    useUpdateCostMetadataMutation,
} from "../../datastore/CostMetadataDataStore";
import { queryVariantsWithoutCostMetadata } from "../../datastore/ShopifyProductDataStore";
import { queryAllCostPayoutModifiers } from "../../datastore/CostPayoutModifierDataStore";
import { DefaultCostMetadataTableConfig } from "./cost-metadata/tables/config/DefaultCostMetadataTableConfig";
import { CostMetadataTableCellFactory } from "./cost-metadata/factory/CostMetadataTableCellFactory";
import { TableCellFactory } from "../../common/tables/factory";
import { EmptyState } from "../../common/EmptyState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";
import {
    CostMetadataTableRowSideSheetForm,
    ViewSideSheet,
} from "./cost-metadata/tables/CostMetadataTableRowSideSheetForm";

type CostMetadataWithRelations = CostMetadata & {
    shopifyProductVariant?: ShopifyProductVariant[];
    costPayoutModifier?: CostPayoutModifier | null;
};

const VariantsViewSideSheet: FC<{
    isShown: boolean;
    variants: ShopifyProductVariant[];
    onDismiss: () => void;
}> = ({ isShown, variants, onDismiss }) => {
    return (
        <ViewSideSheet isShown={isShown} title={"Variants"} onDismiss={onDismiss}>
            {variants.length === 0 ? (
                <Paragraph color={"muted"}>No variants linked</Paragraph>
            ) : (
                variants.map((variant: ShopifyProductVariant) => (
                    <Pane
                        key={variant.id}
                        padding={12}
                        marginBottom={8}
                        borderRadius={4}
                        border={"1px solid #e4e7eb"}
                    >
                        <Heading size={400}>{variant.sku || "No SKU"}</Heading>
                        <Paragraph size={300} color={"muted"}>{variant.title || "Untitled"}</Paragraph>
                        <Paragraph size={300} color={"muted"}>ID: {String(variant.id)}</Paragraph>
                    </Pane>
                ))
            )}
        </ViewSideSheet>
    );
};

const ModifierViewSideSheet: FC<{
    isShown: boolean;
    modifier: CostPayoutModifier | null;
    onDismiss: () => void;
}> = ({ isShown, modifier, onDismiss }) => {
    return (
        <ViewSideSheet isShown={isShown} title={"Cost Payout Modifier"} onDismiss={onDismiss}>
            {modifier ? (
                <Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Name</Heading>
                        <Paragraph>{modifier.name}</Paragraph>
                    </Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Country Code</Heading>
                        <Paragraph>{modifier.conditionCountryCode || "—"}</Paragraph>
                    </Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Priority</Heading>
                        <Paragraph>{modifier.priority}</Paragraph>
                    </Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Source Name</Heading>
                        <Paragraph>{modifier.conditionSourceName || "—"}</Paragraph>
                    </Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Fulfillment Service</Heading>
                        <Paragraph>{modifier.conditionFulfillmentService || "—"}</Paragraph>
                    </Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Is Bundle</Heading>
                        <Paragraph>{modifier.conditionIsBundle === true ? "Yes" : modifier.conditionIsBundle === false ? "No" : "—"}</Paragraph>
                    </Pane>
                    <Pane marginBottom={12}>
                        <Heading size={300}>Email Domains</Heading>
                        <Paragraph>{modifier.conditionEmailDomains?.join(", ") || "—"}</Paragraph>
                    </Pane>
                </Pane>
            ) : (
                <Paragraph color={"muted"}>No modifier data</Paragraph>
            )}
        </ViewSideSheet>
    );
};

export const CostMetadataTab: FC<{ shopifyProductId?: string | null }> = ({ shopifyProductId }) => {
    if (!shopifyProductId) {
        return (
            <Pane padding={24}>
                <EmptyState
                    title="No Shopify product linked"
                    subtitle="This product listing is not linked to a Shopify product"
                >
                    <InfoSignIcon size={60} />
                </EmptyState>
            </Pane>
        );
    }

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <CostMetadataTableContent shopifyProductId={shopifyProductId} />
        </RampartAuthorized>
    );
};

const CostMetadataTableContent: FC<{ shopifyProductId: string }> = ({ shopifyProductId }) => {
    const defaultCreateInput: Partial<CostMetadataCreateInput> = useMemo(() => {
        return {
            variantIds: [],
        };
    }, []);

    const [showCreateRowForm, setShowCreateRowForm] = useState<boolean>(false);
    const [createInput, setCreateInput] = useState<Partial<CostMetadataCreateInput>>(defaultCreateInput);
    const [availableVariants, setAvailableVariants] = useState<ShopifyProductVariant[]>([]);
    const [costPayoutModifiers, setCostPayoutModifiers] = useState<CostPayoutModifier[]>([]);

    // Variants cell-click side sheet state
    const [variantsSideSheet, setVariantsSideSheet] = useState<{ isShown: boolean; variants: ShopifyProductVariant[]; costMetadataId: string }>({
        isShown: false,
        variants: [],
        costMetadataId: "",
    });

    // Modifiers cell-click side sheet state
    const [modifiersSideSheet, setModifiersSideSheet] = useState<{ isShown: boolean; modifier: CostPayoutModifier | null }>({
        isShown: false,
        modifier: null,
    });

    // Load available variants and CPMs when side sheet opens
    useEffect(() => {
        if (showCreateRowForm) {
            queryVariantsWithoutCostMetadata(shopifyProductId)
                .then(setAvailableVariants)
                .catch(() => setAvailableVariants([]));
            queryAllCostPayoutModifiers()
                .then(setCostPayoutModifiers)
                .catch(() => setCostPayoutModifiers([]));
        }
    }, [showCreateRowForm, shopifyProductId]);

    const onVariantsCellClick = useCallback((rowData: CostMetadata) => {
        const data = rowData as CostMetadataWithRelations;
        const variants = data.shopifyProductVariant ?? [];
        setVariantsSideSheet({
            isShown: true,
            variants,
            costMetadataId: rowData.id,
        });
    }, []);

    const onModifiersCellClick = useCallback((rowData: CostMetadata) => {
        const data = rowData as CostMetadataWithRelations;
        const modifier = data.costPayoutModifier ?? null;
        if (modifier) {
            setModifiersSideSheet({
                isShown: true,
                modifier,
            });
        }
    }, []);

    const costMetadataTableCellFactory: TableCellFactory<CostMetadataTableRow, CostMetadata> = useMemo(() => {
        return new CostMetadataTableCellFactory({
            onVariantsCellClick,
            onModifiersCellClick,
        });
    }, [onVariantsCellClick, onModifiersCellClick]);

    const queryTableData = useCallback((input: QueryInput<CostMetadataTableRow>) => {
        const variantSkusExpr = input.expressions.find(e => e.columnName === 'variantSkus');
        const startDateExpr = input.expressions.find(e => e.columnName === 'startDate');

        return queryCostMetadataByShopifyProductId({
            shopifyProductId,
            variantId: variantSkusExpr?.columnValue?.[0] as string | undefined,
            startDateFrom: startDateExpr?.columnValue?.[0] as string | undefined,
            startDateTo: startDateExpr?.columnValue?.[1] as string | undefined,
            options: {
                take: input.pageSize,
            },
        });
    }, [shopifyProductId]);

    const [
        createCostMetadata,
        {
            loading: createLoading,
            error: createError,
            called: createCalled,
        }
    ] = useCreateCostMetadataMutation();

    const [
        updateCostMetadata,
        {
            loading,
            called,
            error,
        }
    ] = useUpdateCostMetadataMutation();

    const onOpenSideSheet = useCallback(() => {
        setShowCreateRowForm(true);
    }, []);

    const onSubmitSideSheet = useCallback(() => {
        createCostMetadata(createInput as CostMetadataCreateInput);
    }, [createCostMetadata, createInput]);

    const onCancelSideSheet = useCallback(() => {
        setShowCreateRowForm(false);
    }, []);

    const onUpdateRow = useCallback((
        costMetadataList: CostMetadata[],
        id: string | number,
        columnName: keyof CostMetadataTableRow,
        value: string | number | boolean | null,
        _label?: string,
    ): CostMetadata[] => {
        return costMetadataList.map((costMetadata: CostMetadata) => {
            if (costMetadata.id !== id) {
                return costMetadata;
            }

            const costMetadataToUpdate = {
                ...costMetadata,
                [columnName]: value,
            };

            updateCostMetadata(costMetadataToUpdate);

            return costMetadataToUpdate;
        });
    }, [updateCostMetadata]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No cost metadata found",
            secondaryText: "Add Cost Metadata",
            onClickSecondaryText: () => setShowCreateRowForm(true),
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load cost metadata",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    // Reset form state after successful creation
    useEffect(() => {
        if (!createCalled) {
            return;
        }

        if (!createLoading && !createError) {
            setCreateInput(defaultCreateInput);
            setShowCreateRowForm(false);
        }
    }, [createLoading, createCalled, createError, defaultCreateInput]);

    return (
        <>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={loading}
                updateRowCalled={called}
                updateRowError={error}
                createRowCalled={createCalled}
                createRowError={createError}
                createRowLoading={createLoading}
                tableCellFactory={costMetadataTableCellFactory}
                savedTableConfigProps={{
                    tableName: "costMetadata",
                    identifiers: ["v1"],
                    defaultConfigValue: DefaultCostMetadataTableConfig,
                }}
            >
                <TableView
                    errorViewProps={errorViewProps}
                    emptyViewProps={emptyViewProps}
                />
            </AbstractTableViewProvider>
            <CostMetadataTableRowSideSheetForm
                isShown={showCreateRowForm}
                costMetadata={createInput}
                variants={availableVariants}
                costPayoutModifiers={costPayoutModifiers}
                mutationLoading={createLoading}
                mutationCalled={createCalled}
                mutationError={createError}
                onDismiss={onCancelSideSheet}
                onOpen={onOpenSideSheet}
                onSubmit={onSubmitSideSheet}
                onInputChanged={setCreateInput}
            />
            <VariantsViewSideSheet
                isShown={variantsSideSheet.isShown}
                variants={variantsSideSheet.variants}
                onDismiss={() => setVariantsSideSheet({ isShown: false, variants: [], costMetadataId: "" })}
            />
            <ModifierViewSideSheet
                isShown={modifiersSideSheet.isShown}
                modifier={modifiersSideSheet.modifier}
                onDismiss={() => setModifiersSideSheet({ isShown: false, modifier: null })}
            />
        </>
    );
};
